import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { formatLinkBlockToUrl, formatUrlWithActiveFund } from '$utilities/format';
import { isLocalURL } from '$utilities/browser';
import { IconLogo } from '$partials/icons';
import HeaderDialog from './header-dialog';
import { getSiteContext } from '$utilities/site-context';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const Header = ({
    branding = getSiteContext()?.branding,
    siteName = getSiteContext()?.siteName,
    navigation,
}) => {
    const [isNavOpen, setIsNavOpen] = useState(false);

    const siteContext = getSiteContext();

    return (
        <header className='relative layout-container-padding-right flex flex-wrap max-w-container pt-75 sm:pt-60 md:pt-45'>
            {/* Sidebar */}
            <div className='w-sidebar text-center'>
                <a
                    className='inline-flex'
                    href={siteContext.fundSlug ? `/${siteContext.fundSlug}` : '/'}>
                    <span
                        aria-hidden={true}
                        className='block'>
                        <IconLogo branding={branding} />
                    </span>
                </a>
            </div>

            {/* Main content */}
            <div className='flex-1 flex flex-wrap items-start md:items-center layout-negative-margin'>
                <div className='w-9/12 self-center md:self-auto layout-item-padding'>
                    <a
                        className='inline-flex type-style-4'
                        href={siteContext.fundSlug ? `/${siteContext.fundSlug}` : '/'}>
                        {siteName}
                    </a>
                </div>
                {/* Quick links */}
                <div className='w-3/12 layout-item-padding'>
                    <div className='flex justify-end'>
                        {siteContext?.quickLinks?.length > 0 && (
                            <nav
                                aria-label='Quick links'
                                className='px-20 rounded-full type-style-7 bg-white hidden md:flex md:gap-x-20'
                                id='primary-navigation'>
                                {siteContext?.quickLinks.map((link, i) => {
                                    const href = formatUrlWithActiveFund(formatLinkBlockToUrl(link.link));
                                    const target = isLocalURL(href) ?  '_self' : '_blank';
                                    const rel = isLocalURL(href) ? '' : 'noreferrer noopener';

                                    if (!href) {
                                        return null;
                                    } else {
                                        return <a
                                            className='py-12'
                                            href={href}
                                            key={i}
                                            rel={rel}
                                            target={target}>
                                            {link.label || link.link?.page?.title }
                                        </a>;
                                    }
                                })}
                            </nav>
                        )}

                        {/* Burger */}
                        {(navigation?.links?.length > 0 || navigation?.quickLinks?.length > 0) && (
                            <>
                                {/* Open navigation */}
                                <button
                                    aria-expanded={isNavOpen}
                                    aria-label={'Open main navigation'}
                                    className='rounded-full bg-white block md:ml-10 md:-mr-75 p-12 burger-menu-button'
                                    onClick={setIsNavOpen.bind(null, true)}
                                    type='button'>
                                    <div className='burger-menu'>
                                        <span className='burger-line-top'></span>
                                        <span className=''></span>
                                        <span className='burger-line-bottom'></span>
                                    </div>
                                </button>

                                {/* Dialog */}
                                <HeaderDialog
                                    closeNav={setIsNavOpen.bind(null, false)}
                                    funds={navigation.funds}
                                    isNavOpen={isNavOpen}
                                    links={navigation.links}/>
                            </>
                        )}
                    </div>
                </div>

            </div>
        </header>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const navigationLink = PropTypes.shape({
    page: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        url: PropTypes.string,
    }),
});

const propTypes = {
    branding: PropTypes.string,
    siteName: PropTypes.string,
    navigation: PropTypes.shape({
        quickLinks: PropTypes.arrayOf(navigationLink),
        links: PropTypes.arrayOf(navigationLink),
        funds: PropTypes.arrayOf(navigationLink),
    }),
};

Header.propTypes = propTypes;

export default Header;
